import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import logoEdb from "@/assets/logo-edb.jpg";
import logoOracle from "@/assets/logo-oracle.png";
import logoAlteryx from "@/assets/logo-alteryx-4.png";
import logoDataiku from "@/assets/logo-dataiku.png";
import logoDenodo from "@/assets/logo-denodo.png";
import logoQlik from "@/assets/logo-qlik.png";
import logoConfluent from "@/assets/logo-confluent.jpg";
import logoFivetran from "@/assets/logo-fivetran.png";
import logoAstronomer from "@/assets/logo-astronomer.png";
import { Button } from "@/components/ui/button";
import { Download, Eye } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Awards = () => {
  const { t } = useLanguage();
  const [selectedCert, setSelectedCert] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const dataikuCertificates = [
    {
      title: "Dataiku Core Designer",
      issuer: "Dataiku",
      date: "May 19, 2025",
      thumbnail: "/certificates/thumbnails/core-designer.jpg",
      pdfUrl: "/certificates/Dataiku_Designer_Core_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/ctq8zd6inaww",
    },
    {
      title: "Dataiku Advanced Designer",
      issuer: "Dataiku",
      date: "May 20, 2025",
      thumbnail: "/certificates/thumbnails/advanced-designer.jpg",
      pdfUrl: "/certificates/Dataiku_Advance_Designer_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/crrunkbfjvx6",
    },
    {
      title: "Dataiku ML Practitioner",
      issuer: "Dataiku",
      date: "May 20, 2025",
      thumbnail: "/certificates/thumbnails/ml-practitioner.jpg",
      pdfUrl: "/certificates/Dataiku_ML_Practitioner_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/tzjeyae9c83f",
    },
    {
      title: "Dataiku Developer",
      issuer: "Dataiku",
      date: "May 21, 2025",
      thumbnail: "/certificates/thumbnails/developer.jpg",
      pdfUrl: "/certificates/Dataiku_Developer_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/8a9tm7ef577g",
    },
    {
      title: "Dataiku MLOps Practitioner",
      issuer: "Dataiku",
      date: "May 21, 2025",
      thumbnail: "/certificates/thumbnails/mlops-practitioner.jpg",
      pdfUrl: "/certificates/Dataiku_MLOps_Practitioner_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/rzdt6r5jvifw",
    },
    {
      title: "Dataiku Generative AI Practitioner",
      issuer: "Dataiku",
      date: "May 21, 2025",
      thumbnail: "/certificates/thumbnails/genai-practitioner.jpg",
      pdfUrl: "/certificates/Dataiku_Generative_AI_Practitioner_Certificate.pdf",
      credentialUrl: "https://verify.skilljar.com/c/tt4635m8zhb9",
    },
  ];

  const alteryxCertificates = [
    {
      title: "Alteryx Server Administration",
      issuer: "Alteryx",
      date: "May 27, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-server-implementation.jpg",
      pdfUrl: "/certificates/Alteryx_Server_Implementation.pdf",
      credentialUrl: "https://www.credly.com/badges/12e23a84-d931-4b13-bc44-895f090979cf/public_url",
    },
    {
      title: "Alteryx Server Implementation",
      issuer: "Alteryx",
      date: "May 28, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-server-implementation.jpg",
      pdfUrl: "/certificates/Alteryx_Server_Implementation.pdf",
      credentialUrl: "https://www.credly.com/badges/dc3a3fe6-2912-43b5-bbfe-83b5353795c0/public_url",
    },
    {
      title: "Alteryx Designer Core",
      issuer: "Alteryx",
      date: "Aug 16, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-designer-core.jpg",
      pdfUrl: "/certificates/Alteryx_Designer_Core.pdf",
      credentialUrl: "https://www.credly.com/badges/f6e38c1c-ff35-4ec1-8c3a-329c4aa9c239/public_url",
    },
    {
      title: "Alteryx Designer Advanced",
      issuer: "Alteryx",
      date: "Aug 21, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-designer-advance.jpg",
      pdfUrl: "/certificates/Alteryx_Designer_Advance.pdf",
      credentialUrl: "https://www.credly.com/badges/3274201c-b3a8-4709-8e44-b03bf252c2be/public_url",
    },
    {
      title: "Alteryx Designer Cloud Core",
      issuer: "Alteryx",
      date: "May 22, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-cloud-core.jpg",
      pdfUrl: "/certificates/Alteryx_Designer_Cloud_Core.pdf",
      credentialUrl: "https://www.credly.com/badges/99187d5c-2ebb-4035-a212-4ee1bc3d75e2/public_url",
    },
    {
      title: "Alteryx Designer Cloud Trifacta Classic Core",
      issuer: "Alteryx",
      date: "Aug 20, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-trifacta-core.jpg",
      pdfUrl: "/certificates/Alteryx_Designer_Cloud_Trifacta_Classic_Core.pdf",
      credentialUrl: "https://www.credly.com/badges/82104ebd-9f4b-4b78-98df-efbd45421910/public_url",
    },
    {
      title: "Alteryx Designer Cloud Trifacta Classic Administrator",
      issuer: "Alteryx",
      date: "Sep 23, 2025",
      thumbnail: "/certificates/thumbnails/alteryx-trifacta-administrator.jpg",
      pdfUrl: "/certificates/Alteryx_Trifacta_Administrator.pdf",
      credentialUrl: "https://www.credly.com/badges/12e23a84-d931-4b13-bc44-895f090979cf/public_url",
    },
  ];

  const astronomerCertificates = [
    {
      title: "Astronomer Certification for Apache Airflow 3 Fundamentals",
      issuer: "Astronomer",
      date: "Sep 18, 2025",
      thumbnail: "/certificates/thumbnails/astronomer-airflow-fundamentals.jpg",
      pdfUrl: "/certificates/Astronomer_Airflow_3_Fundamentals.pdf",
      credentialUrl: "https://www.credly.com/badges/40ac09ba-00f6-42e3-8807-726dde747ab5/public_url",
    },
  ];

  const fivetranCertificates = [
    {
      title: "Fivetran Connector SDK Certification",
      issuer: "Fivetran",
      date: "Sep 1, 2025",
      thumbnail: "/certificates/thumbnails/fivetran-connector-sdk.jpg",
      pdfUrl: "/certificates/Fivetran_Connector_SDK_Certification.pdf",
      credentialUrl: "https://drive.google.com/file/d/10_On8gGizB8GaJTsRmPD7cNpGZpUyTaC/view?usp=drive_open",
    },
  ];

  const confluentCertificates = [
    {
      title: "Confluent Data Streaming Engineer",
      issuer: "Confluent",
      date: "2025",
      thumbnail: "/certificates/thumbnails/confluent-data-streaming-engineer.png",
      pdfUrl: "",
      credentialUrl: "https://certificates.confluent.io/f197d84c-8dd7-40c1-9696-e26e8516e181",
    },
  ];

  const postgresCertificates = [
    {
      title: "EDB Postgres AI Database Essentials v17",
      issuer: "EDB",
      date: "Aug 19, 2025",
      thumbnail: "/certificates/thumbnails/edb-postgres-ai-v17-essentials.jpg",
      pdfUrl: "/certificates/EDB_Postgres_AI_v17_Essentials.pdf",
      credentialUrl: "https://www.credly.com/badges/686beaaa-323c-48a8-b26d-c8663aaba912/public_url",
    },
    {
      title: "EDB Postgres AI Database Advanced v17",
      issuer: "EDB",
      date: "Aug 26, 2025",
      thumbnail: "/certificates/thumbnails/edb-postgres-ai-v17-advance.jpg",
      pdfUrl: "/certificates/EDB_Postgres_AI_v17_Advance.pdf",
      credentialUrl: "https://www.credly.com/badges/41dfb048-8198-42f7-b0ab-cd165a921a28/public_url",
    },
    {
      title: "PostgreSQL Essentials v16",
      issuer: "PostgreSQL / EDB",
      date: "May 27, 2025",
      thumbnail: "/certificates/thumbnails/postgresql-v16-essentials.jpg",
      pdfUrl: "/certificates/PostgreSQL_v16_Essentials.pdf",
      credentialUrl: "https://www.credly.com/badges/2d9d6ad9-ab02-4d25-bdc6-47035dc78a33/public_url",
    },
    {
      title: "PostgreSQL Advanced v16",
      issuer: "PostgreSQL / EDB",
      date: "Jun 9, 2025",
      thumbnail: "/certificates/thumbnails/postgresql-v16-advance.jpg",
      pdfUrl: "/certificates/PostgreSQL_v16_Advance.pdf",
      credentialUrl: "https://www.credly.com/badges/ffceb401-f720-4c60-81c7-ac855739b76a/public_url",
    },
    {
      title: "EDB Postgres 15 Essentials",
      issuer: "EDB",
      date: "May 30, 2025",
      thumbnail: "/certificates/thumbnails/edb-postgres-15-essentials.jpg",
      pdfUrl: "/certificates/EDB_Postgres_15_Essentials.pdf",
      credentialUrl: "https://www.credly.com/badges/1a4253a0-81d2-4546-9106-813b6d63d4db/public_url",
    },
    {
      title: "EDB Postgres 15 Advanced",
      issuer: "EDB",
      date: "Jun 25, 2025",
      thumbnail: "/certificates/thumbnails/edb-postgres-15-advance.jpg",
      pdfUrl: "/certificates/EDB_Postgres_15_Advance.pdf",
      credentialUrl: "https://www.credly.com/badges/421270e3-b8b7-4ef9-b736-097437ec3f82/public_url",
    },
  ];

  const denodoCertificates = [
    {
      title: "Denodo Platform 8.0 Certified Architect Associate",
      issuer: "Denodo",
      date: "2025",
      thumbnail: "/certificates/thumbnails/denodo-8-architect.jpg",
      pdfUrl: "/certificates/Denodo_Platform_8.0_Architect.pdf",
      credentialUrl: "https://certification.denodo.com/validation/UY6T7IKOJ9E32BIDVLEO",
    },
    {
      title: "Denodo Platform 9 Certified Architect Associate",
      issuer: "Denodo",
      date: "2025",
      thumbnail: "/certificates/thumbnails/denodo-9-architect.jpg",
      pdfUrl: "/certificates/Denodo_Platform_9_Architect.pdf",
      credentialUrl: "https://certification.denodo.com/validation/S0TBLFXU3G2BPMCH3C7I",
    },
  ];

  const oracleCertificates = [
    {
      title: "Oracle Database@AWS Certified Architect Professional",
      issuer: "Oracle",
      date: "Oct 28, 2025",
      thumbnail: "/certificates/thumbnails/oracle-database-aws-architect-professional.jpg",
      pdfUrl: "/certificates/Oracle_Database@AWS_Certified_Architect_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=C35C2C37ECDC78DFCBCEA4BB43A6F4286E18D82744838D7B6620B27F44A009DA",
    },
    {
      title: "Oracle Autonomous Database Cloud 2025 Certified Professional",
      issuer: "Oracle",
      date: "Oct 13, 2025",
      thumbnail: "/certificates/thumbnails/oracle-autonomous-database-professional.jpg",
      pdfUrl: "/certificates/Oracle_Autonomous_Database_2025_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=9536D5DFD9601DC51D06FCDE67C20FC4E94E2A6EAAA2E32E0624C13ABAA81986",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 Certified Multicloud Architect Professional",
      issuer: "Oracle",
      date: "Oct 22, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-multicloud-architect-professional.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_Multicloud_Architect_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=1D1EAD6FFD77E03B8DCE9A0669973B8B5CC7BAF60CBCCCB4FAE4D2455F7F1215",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 Migration Architect Certified Professional",
      issuer: "Oracle",
      date: "Oct 13, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-migration-architect-professional.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_Migration_Architect_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=8BA4972BD18657C3DA2DAA42AD74BFD73215F5057822DEC2B5DC9F0CB78791FD",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 Certified Data Science Professional",
      issuer: "Oracle",
      date: "Oct 22, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-data-science-professional.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_Data_Science_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=DE8DA34189E7127102483F605C9F51621F08FEC2CBF5961EE5D06DE57E77060A",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 Certified Generative AI Professional",
      issuer: "Oracle",
      date: "Oct 9, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-genai-professional.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_GenAI_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=FA1E9B39F89D27F5302157349C63BF485FE801ECB7ECD1101EE7039C836D3660",
    },
    {
      title: "Oracle AI Vector Search Certified Professional",
      issuer: "Oracle",
      date: "Oct 9, 2025",
      thumbnail: "/certificates/thumbnails/oracle-ai-vector-search-professional.jpg",
      pdfUrl: "/certificates/Oracle_AI_Vector_Search_Professional.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=13250A0E5A729FCEB24D87F11CFF73F8C09DA057EAE6C5005F5E60AEFFAF1E3F",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 AI Foundations Associate",
      issuer: "Oracle",
      date: "Jun 18, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-ai-foundations.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_AI_Foundations.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=731EEED1E183528179CBA7F72FE4A5C9064BBCBE9B2C03B14A5FCECAB3A1439E",
    },
    {
      title: "Oracle Cloud Infrastructure 2025 Foundations Associate",
      issuer: "Oracle",
      date: "Jun 24, 2025",
      thumbnail: "/certificates/thumbnails/oracle-oci-foundations.jpg",
      pdfUrl: "/certificates/Oracle_OCI_2025_Foundations.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=AE675E8668287DEF894349590AF15654915E52FAC0DA320E0AF1B95DE937DAA0",
    },
    {
      title: "Oracle Data Platform 2025 Foundations Associate",
      issuer: "Oracle",
      date: "May 28, 2025",
      thumbnail: "/certificates/thumbnails/oracle-data-platform-foundations.jpg",
      pdfUrl: "/certificates/Oracle_Data_Platform_2025_Foundations.pdf",
      credentialUrl:
        "https://catalog-education.oracle.com/ords/certview/sharebadge?id=DE8DA34189E7127102483F605C9F51627C636FE139AB31FEBE566214FDE36326",
    },
  ];

  const qlikCertificates = [
    {
      title: "Qlik Certified Partner Sales – Analytics",
      issuer: "Qlik",
      date: "Sep 18, 2025",
      thumbnail: "/certificates/thumbnails/qlik-partner-analytics.jpg",
      pdfUrl: "/certificates/Qlik_Partner_Sales_Analytics.pdf",
      credentialUrl: "https://learning.qlik.com/student/award/RmqUEkDj6sc2HqFfk8dR9ycD",
    },
    {
      title: "Qlik Certified Partner Sales – Data and Analytics",
      issuer: "Qlik",
      date: "Sep 18, 2025",
      thumbnail: "/certificates/thumbnails/qlik-partner-data-analytics.jpg",
      pdfUrl: "/certificates/Qlik_Partner_Sales_Data_Analytics.pdf",
      credentialUrl: "https://learning.qlik.com/student/award/xjNr4eTzBLQnuFCxSuDbMGPj",
    },
    {
      title: "Qlik Certified Partner Sales – Data Integration",
      issuer: "Qlik",
      date: "Sep 18, 2025",
      thumbnail: "/certificates/thumbnails/qlik-partner-data-integration.jpg",
      pdfUrl: "/certificates/Qlik_Partner_Sales_Data_Integration.pdf",
      credentialUrl: "https://learning.qlik.com/student/award/ZhMcejC8sRXVEnJLvTmLA9Kc",
    },
  ];

  const awardCategories = [
    {
      category: "PostgreSQL & EDB",
      items: [],
    },
    {
      category: "Oracle Cloud & AI",
      items: [],
    },
    {
      category: "Data Science & AI (Alteryx, Dataiku)",
      items: [],
    },
    {
      category: "Denodo Platform",
      items: [],
    },
    {
      category: "Business Intelligence & Qlik",
      items: [],
    },
    {
      category: "Integration & Streaming",
      items: [],
    },
    {
      category: "Workflow Orchestration",
      items: [],
    },
  ];

  const displayedCategories = showAll ? awardCategories : awardCategories.slice(0, 2);

  return (
    <section id="awards" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.awards.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">{t.awards.subtitle}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3 sm:space-y-4">
            {displayedCategories.map((category, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card border border-border rounded-lg px-4 sm:px-5 md:px-6 shadow-soft animate-fade-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <AccordionTrigger className="hover:no-underline py-3 sm:py-4">
                  <div className="flex items-center justify-between w-full pr-2 sm:pr-3 md:pr-4">
                    <span className="text-base sm:text-lg md:text-xl font-semibold text-left">{category.category}</span>
                    <Badge variant="secondary" className="ml-2 sm:ml-3 md:ml-4 text-xs sm:text-sm flex-shrink-0">
                      {category.category === "PostgreSQL & EDB"
                        ? postgresCertificates.length
                        : category.category === "Data Science & AI (Alteryx, Dataiku)"
                          ? category.items.length + alteryxCertificates.length + dataikuCertificates.length
                          : category.category === "Oracle Cloud & AI"
                            ? category.items.length + oracleCertificates.length
                            : category.category === "Denodo Platform"
                              ? denodoCertificates.length
                              : category.category === "Business Intelligence & Qlik"
                                ? qlikCertificates.length
                                : category.category === "Workflow Orchestration"
                                  ? astronomerCertificates.length
                                  : category.category === "Integration & Streaming"
                                    ? fivetranCertificates.length + confluentCertificates.length
                                    : category.items.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4 pt-4">
                    {category.category === "PostgreSQL & EDB" ? (
                      <>
                        {/* PostgreSQL/EDB Certificates with Images */}
                        {postgresCertificates.map((cert, certIndex) => (
                          <div
                            key={`postgres-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoEdb}
                                alt="EDB logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Data Science & AI (Alteryx, Dataiku)" ? (
                      <>
                        {/* Alteryx Certificates */}
                        {category.items.map((cert, certIndex) => (
                          <div
                            key={certIndex}
                            className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 flex-1">
                              {cert.logo && (
                                <img
                                  src={cert.logo}
                                  alt={`${cert.issuer} logo`}
                                  className="w-10 h-10 object-contain rounded flex-shrink-0"
                                />
                              )}
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                              </div>
                            </div>
                            <a
                              href={cert.credentialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary hover:underline whitespace-nowrap"
                            >
                              {t.awards.view}
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        ))}

                        {/* Alteryx Certificates with Images */}
                        {alteryxCertificates.map((cert, certIndex) => (
                          <div
                            key={`alteryx-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoAlteryx}
                                alt="Alteryx logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Dataiku Certificates with Images */}
                        {dataikuCertificates.map((cert, certIndex) => (
                          <div
                            key={`dataiku-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoDataiku}
                                alt="Dataiku logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Oracle Cloud & AI" ? (
                      <>
                        {/* Oracle Certificates with Images */}
                        {oracleCertificates.map((cert, certIndex) => (
                          <div
                            key={`oracle-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoOracle}
                                alt="Oracle logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Other Oracle Certificates (without PDFs) */}
                        {category.items.map((cert, certIndex) => (
                          <div
                            key={certIndex}
                            className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 flex-1">
                              {cert.logo && (
                                <img
                                  src={cert.logo}
                                  alt={`${cert.issuer} logo`}
                                  className="w-10 h-10 object-contain rounded flex-shrink-0"
                                />
                              )}
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                              </div>
                            </div>
                            <a
                              href={cert.credentialUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary hover:underline whitespace-nowrap"
                            >
                              {t.awards.view}
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Denodo Platform" ? (
                      <>
                        {/* Denodo Certificates with Images */}
                        {denodoCertificates.map((cert, certIndex) => (
                          <div
                            key={`denodo-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoDenodo}
                                alt="Denodo logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Business Intelligence & Qlik" ? (
                      <>
                        {/* Qlik Certificates with Images */}
                        {qlikCertificates.map((cert, certIndex) => (
                          <div
                            key={`qlik-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoQlik}
                                alt="Qlik logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Workflow Orchestration" ? (
                      <>
                        {/* Astronomer Certificates with Images */}
                        {astronomerCertificates.map((cert, certIndex) => (
                          <div
                            key={`astronomer-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoAstronomer}
                                alt="Astronomer logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.pdfUrl} download>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : category.category === "Integration & Streaming" ? (
                      <>
                        {/* Fivetran Certificates with Images */}
                        {fivetranCertificates.map((cert, certIndex) => (
                          <div
                            key={`fivetran-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoFivetran}
                                alt="Fivetran logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                              {cert.pdfUrl && (
                                <Button variant="outline" size="sm" asChild>
                                  <a href={cert.pdfUrl} download>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </a>
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}

                        {/* Confluent Certificates with Images */}
                        {confluentCertificates.map((cert, certIndex) => (
                          <div
                            key={`confluent-${certIndex}`}
                            className="p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-start gap-3 mb-3">
                              <img
                                src={logoConfluent}
                                alt="Confluent logo"
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                              <div>
                                <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                                <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                                <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button variant="outline" size="sm" onClick={() => setSelectedCert(cert.thumbnail)} aria-label={`View ${cert.title} certificate image`}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Image
                              </Button>
                              <Button variant="outline" size="sm" asChild>
                                <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  View Credential
                                </a>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      category.items.map((cert, certIndex) => (
                        <div
                          key={certIndex}
                          className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start gap-3 flex-1">
                            {cert.logo && (
                              <img
                                src={cert.logo}
                                alt={`${cert.issuer} logo`}
                                className="w-10 h-10 object-contain rounded flex-shrink-0"
                              />
                            )}
                            <div>
                              <h4 className="font-medium text-foreground mb-1">{cert.title}</h4>
                              <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                            </div>
                          </div>
                          <a
                            href={cert.credentialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-primary hover:underline whitespace-nowrap"
                          >
                            {t.awards.view}
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {awardCategories.length > 2 && (
            <div className="flex justify-center mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowAll(!showAll)}
                className="min-w-[200px]"
              >
                {showAll ? t.portfolio.showLess : t.portfolio.showMore}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!selectedCert} onOpenChange={() => setSelectedCert(null)}>
        <DialogContent className="max-w-fit max-h-[95vh] p-2">
          <DialogHeader className="px-4 pt-2 pb-2">
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2 bg-muted/30 rounded-lg">
            {selectedCert && (
              <img
                src={selectedCert}
                alt="Certificate preview"
                className="w-auto h-auto max-w-[90vw] max-h-[82vh] object-contain rounded"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Awards;
